import type { NuxtError } from "#app";
import { HTTPError, TimeoutError, type Options } from "ky";
import type { Address as AddressDto } from "~/dto/address";
import type { Cart as CartDto, CartItem as CartItemDto } from "~/dto/cart";
import type { Category as CategoryDto } from "~/dto/category";
import type { AutocompleteCityDto } from "~/dto/city";
import type { DocDto } from "~/dto/doc";
import { type Phone as PhoneDto } from "~/dto/phone";
import type { AssociatedProductsDto, Product as ProductDto, ProductWithVariants as ProductWithVariantsDto } from "~/dto/product";
import type { ProductReview as ProductReviewDto } from "~/dto/product-review";
import type { RequestQuoteDto } from "~/dto/request-quote";
import type { UserDto } from "~/dto/user";
import { useCartStore } from "~/store/cart";

export type ImageSize = "m" | "s" | String;
export type LogoType = "contacts" | "brands";

export namespace ApiPdg {
  export interface Cart {
    cart: CartDto,
    cartItems: CartItemDto[]
  }

  export interface FacetCountDto {
    name: string;
    field_name: string;
    display_order?: number | null;
    sampled: boolean;
    counts: {
      count: number;
      highlighted: string;
      value: string;
    }[];
    stats: {
      // Nombre d'éléments, activables et non activables
      total_values: number;
      // Nombre d'éléments, activables
      facet_values: number;
    };
  }

  export interface Products {
    // eslint-disable-next-line camelcase
    facet_counts: FacetCountDto[],
    found: number,
    hits: ProductDto[];
    // eslint-disable-next-line camelcase
    out_of: number;
    page: number;
    // eslint-disable-next-line camelcase
    search_time: number;
    // eslint-disable-next-line camelcase
    search_cutoff: boolean;
    // eslint-disable-next-line camelcase
    request_params: any;
  }

  export interface UserDataType {
    value: number;
    text: string;
  }

  export interface UserPhones {
    count: number;
    data: PhoneDto[];
    // Deprecated
    typeList: UserDataType[];
  }

  export interface UserAddresses {
    count: number;
    data: AddressDto[];
    // Deprecated
    typeList: UserDataType[];
  }

  export interface CreateOrder {
    docId: number;
  }

  export interface BannerDto {
    id: number;
    media_name: string;
    url?: string;
    alt?: string;
    width: number;
    height: number;
  }

  export interface ShipmentDto {
    id?: number;
    doc_id?: number;
    carrier?: string | null;
    tracking_numbers?: string | null;
    comment?: string | null;
    initial_status?: string | null;
    created: Date;
    shipment_date: Date | string | null;
    is_delivery_date: boolean;
    approximative_date: boolean;
    lines?: ShipmentLineDto[];
  }

  export interface ShipmentLineDto {
    id?: number;
    shipment_id?: number;
    doc_line_id?: number;
    // product_id?: number;
    label: string;
    reference: string;
    quantity: number;
    max_quantity: number;
  }

  export namespace R {
    export interface Error {
      error?: boolean;
      message?: string;
      errors?: Record<string, string>;
    }
    export interface Base<T> {
      statusCode: number;
      data: T & Error;
    }
    export type Products = Base<ApiPdg.Products>;
    export type Product = Base<ProductDto>;
    export type ProductWithVariants = Base<ProductWithVariantsDto>;
    export type ProductReview = Base<ProductReviewDto>;
    export type ProductReviews = Base<{ data: ProductReviewDto[], count: number }>;
    export type AssociatedProducts = Base<AssociatedProductsDto>;
    export type Category = Base<CategoryDto>;
    export type Categories = Base<CategoryDto[]>;
    export type Cart = Base<ApiPdg.Cart>;
    export type User = Base<UserDto>;
    export type UserPhones = Base<ApiPdg.UserPhones>;
    export type UserAddresses = Base<ApiPdg.UserAddresses>;
    export type UserRequestQuotes = Base<RequestQuoteDto[]>;
    export type CreateOrder = Base<ApiPdg.CreateOrder>;
    export type UsernameAvailable = Base<{ available: boolean }>;
    export type Banners = Base<ApiPdg.BannerDto[]>;
    export type VuetifyList<T> = { count: number, data: T[] };
    export type UserOrders = Base<VuetifyList<DocDto>>;
    export type UserDocShipments = Base<VuetifyList<ShipmentDto>>;
  }
}

type FrontError<DataT = unknown> = Partial<NuxtError<DataT>> & {
  status?: number;
  statusText?: string;
};

export async function tranformToNuxtError(error: Error): Promise<never> {
  if (error instanceof TimeoutError) {
    throw createError<FrontError>({ cause: 'timeout', message: 'Timeout', status: 408, statusText: 'Request Timeout' });
  } else if (error instanceof HTTPError) {
    throw createError<FrontError>({ status: error.response.status, statusText: error.response.statusText, statusCode: error.response.status, data: await error.response?.json(), message: error.response.statusText, statusMessage: error.response.statusText });
  }
  throw error;
}

export default defineNuxtPlugin(function () {
  const { $apiHttp, $pinia } = useNuxtApp();
  const cartStore = useCartStore($pinia);

  const apiPdg = {
    searchProducts: async (filters?: any, keywords: string = '*', options?: { page?: number, perPage?: number }): Promise<ApiPdg.Products> => {
      const searchParams: any = {
        page: options?.page ?? 1,
        per_page: options?.perPage ?? 10,
      };

      if (keywords) {
        searchParams.keywords = keywords;
      }
      if (cartStore.shipping.countryCode) {
        searchParams.country_code = cartStore.shipping.countryCode;
      }
      if (cartStore.shipping.zipCode) {
        searchParams.zip_code = cartStore.shipping.zipCode;
      }
      if (filters) {
        searchParams.filters = filters;
      }

      return (await $apiHttp.get<ApiPdg.R.Products>("products/search", { searchParams: searchParams }).json()).data;
    },
    searchCategories: async (keywords: string): Promise<ApiPdg.Products> => (await $apiHttp.get("categories/search", { searchParams: { keywords } }).json<ApiPdg.R.Products>()).data,
    product: async (productId: number, scc?: string, szc?: string | number): Promise<ProductWithVariantsDto> => {
      // TODO : sortir les variantes dans un ws distinct
      const res = await $apiHttp.get(`products/${productId}`, {
        // Don't send 'null' values as they aren't interpreted by the API
        searchParams: JSON.parse(JSON.stringify({
          scc: scc ?? cartStore.shipping.countryCode ?? undefined,
          szc: szc ?? cartStore.shipping.zipCode ?? undefined,
        })),
      }).json<ApiPdg.R.ProductWithVariants>();
      return res.data;
    },
    userProductReview: async (productId: number): Promise<ProductReviewDto> => (await $apiHttp.get(`user/reviews/${productId}`).json<ApiPdg.R.ProductReview>()).data,
    userOrders: async (filters?: any, options?: any): Promise<ApiPdg.R.VuetifyList<DocDto>> => (await $apiHttp.get(`user/orders`, {
      searchParams: {
        filters: JSON.stringify(filters ?? {}),
        options: JSON.stringify(options ?? {
          itemsPerPage: 100,
          sortBy: ["d.date"],
          sortDesc: [true],
        }),
      }
    }).json<ApiPdg.R.UserOrders>()).data,
    userInvoices: async (): Promise<ApiPdg.R.VuetifyList<DocDto>> => (await $apiHttp.get(`user/invoices`, {
      searchParams: {
        filters: JSON.stringify({}),
        options: JSON.stringify({
          itemsPerPage: 100,
          sortBy: ["d.date"],
          sortDesc: [true],
        }),
      }
    }).json<ApiPdg.R.UserOrders>()).data,
    associatedProducts: async (productId: number): Promise<AssociatedProductsDto> => (await $apiHttp.get(`products/${productId}/related`).json<ApiPdg.R.AssociatedProducts>()).data,
    productReviews: async (productId: number, options?: Options) => (await $apiHttp.get(`products/${productId}/reviews`, options).json<ApiPdg.R.ProductReviews>()).data,
    userProductReviews: async () => (await $apiHttp.get(`user/reviews`).json<ApiPdg.R.Base<(ProductDto & { user_vote: number | null })[]>>()).data,
    saveProductReview: async (data: ProductReviewDto): Promise<void> => (await $apiHttp.post(`user/reviews/${data.product_id}`, { json: data }).json<ApiPdg.R.Base<void>>()).data,
    productByRef: async (productRef: string): Promise<ProductWithVariantsDto> => {
      // TODO : sortir les variantes dans un ws distinct
      const res = await $apiHttp.get(`products/ref/${productRef}`, {
        // Don't send 'null' values as they aren't interpreted by the API
        searchParams: JSON.parse(JSON.stringify({
          scc: cartStore.shipping.countryCode ?? undefined,
          szc: cartStore.shipping.zipCode ?? undefined,
        }),),
      }).json<ApiPdg.R.ProductWithVariants>();
      return res.data;
    },
    category: async (slug: string): Promise<CategoryDto> => (await $apiHttp.get(`categories/${slug}`).json<ApiPdg.R.Category>()).data,
    categories: async (filter?: string): Promise<CategoryDto[]> => (await $apiHttp.get(`categories${filter ? `?filter=${filter}` : ''}`).json<ApiPdg.R.Categories>()).data,
    autocomplete: async (query: string): Promise<any> => (await $apiHttp.get(`search/autocomplete/${query}`).json<ApiPdg.R.Base<any>>()).data, // TODO
    autocompleteCities: async (query: string, limit: number = 10): Promise<AutocompleteCityDto[]> => (await $apiHttp.get(`cities/autocomplete/${query}?limit=${limit}`).json<ApiPdg.R.Base<any>>()).data, // TODO
    banners: async (): Promise<ApiPdg.BannerDto[]> => (await $apiHttp.get(`banners`).json<ApiPdg.R.Banners>()).data,
    brands: async (): Promise<any> => (await $apiHttp.get(`brands`).json<ApiPdg.R.Base<any>>()).data, // TODO
    user: async (): Promise<UserDto> => (await $apiHttp.get(`user/user`).json<ApiPdg.R.User>()).data,
    userPhones: async (): Promise<ApiPdg.UserPhones> => (await $apiHttp.get(`user/phones`).json<ApiPdg.R.UserPhones>()).data,
    userAddresses: async (): Promise<ApiPdg.UserAddresses> => (await $apiHttp.get(`user/addresses`).json<ApiPdg.R.UserAddresses>()).data,
    usernameAvailable: async (username: string) => (await $apiHttp.get(`auth/available/${username}`).json<ApiPdg.R.UsernameAvailable>()).data,
    userRequestQuotes: async () => (await $apiHttp.get("user/requestquotes").json<ApiPdg.R.UserRequestQuotes>()).data,
    userDocShipments: async (docId: number) => (await $apiHttp.get(`user/orders/${docId}/shipments`).json<ApiPdg.R.UserDocShipments>()).data,
  };

  return {
    provide: {
      apiPdg: apiPdg,
    },
  };
});
